import { PRIVATE_ENDPOINTS } from '@/constants/api'
import { deleteData, getData, patchData, postData, postForm } from '@/services/api'
import type {
  IAdv,
  IAdvertiser,
  ICampaign,
  ICourse,
  ICourseRevGolfclub,
  ICustomer,
  ICustomerUser,
  IDanger,
  IDevice,
  IGeoJson,
  IGolfclub,
  IHole,
  IHolePaceStat,
  IIoteeDevice,
  IIoteeLoadHistoryResult,
  IIoteeSite,
  IIoteeSyncDevicesResult,
  IIoteeSyncSitesResult,
  ILayout,
  ILayoutPrint,
  ILayoutTargetPreset,
  ILcClub,
  IMapPoint,
  INotification,
  IPaginatedResponse,
  IPartner,
  IStatistic,
  IStripeInfo,
  ISystemMaintenance,
  ITarget,
  ITrackerPosition,
  IUserEvent,
  IUserHolePace,
  IUserInformation,
  IUserTopic,
} from '@/types'
import { GOLFCLUB_GEOJSON_VERSION } from '@/types/golfclub-geojson'

/**
 * 🏌️‍♂️ Golf Club API Service
 */
export const GolfClubService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IGolfclub>>(PRIVATE_ENDPOINTS.club.all, params),
  getSingle: (id: number) => getData<IGolfclub>(PRIVATE_ENDPOINTS.club.single(id)),
  getGeojson: (id: number, version: string = GOLFCLUB_GEOJSON_VERSION) =>
    getData<{ url: string }>(PRIVATE_ENDPOINTS.club.geojson(id), { version }),
  getStatistics: () =>
    getData<{ ready_not_live: number; live_ready: number; not_ready: number }>(
      PRIVATE_ENDPOINTS.club.statistics,
    ),
  getMapPoints: (id: number, params?: object) =>
    getData<IMapPoint[]>(PRIVATE_ENDPOINTS.club.mapPoints(id), params),
  create: (data: object) => postData<IGolfclub>(PRIVATE_ENDPOINTS.club.create, data),
  update: (id: number, data: object) =>
    patchData<IGolfclub>(PRIVATE_ENDPOINTS.club.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.club.delete(id)),
}

/**
 * 📊 Statistics API Service
 */
export const StatisticsService = {
  getGolfclubDaily: (params?: object) =>
    getData<IPaginatedResponse<IStatistic>>(PRIVATE_ENDPOINTS.stats.golfclubDaily, params),
  getRanking: () =>
    getData<{
      rankingTargets: { id: number; targets: number }[]
      rankingNotifications: { id: number; notifications: number }[]
      rankingMembers: { id: number; members: number }[]
    }>(PRIVATE_ENDPOINTS.stats.ranking),
}

/**
 * ⏱️ Pace of Play API Service
 */
export const PaceService = {
  getUserPace: (params: {
    user_id: number
    golfclub_id: number
    start_date: string
    end_date: string
    course_id?: number
    hole_id?: number
  }) => getData<IUserHolePace[]>(PRIVATE_ENDPOINTS.pace.user, params),
  getTrackerPace: (params: {
    id_customer_device: number
    golfclub_id: number
    start_date: string
    end_date: string
    course_id?: number
    hole_id?: number
  }) => getData<IUserHolePace[]>(PRIVATE_ENDPOINTS.pace.tracker, params),
  getCoursePace: (params: {
    course_id: number
    start_date: string
    end_date: string
    hole_id?: number
  }) => getData<IHolePaceStat[]>(PRIVATE_ENDPOINTS.pace.course, params),
}

/**
 * 📊 Excel API Service
 */
export const ExcelService = {
  createExcel: (data: object) => postData(PRIVATE_ENDPOINTS.excel, data),
}

/**
 * 📤 Upload API Service
 */
export const FileService = {
  uploadFile: (data: object) => postForm(PRIVATE_ENDPOINTS.file.upload, data),
  deleteFile: (path: string) =>
    deleteData(`${PRIVATE_ENDPOINTS.file.delete}?path=${encodeURIComponent(path)}`),
}

/**
 * 👤 Admin User (customers_user) API Service
 */
export const AdminUserService = {
  getAll: (params?: object) => getData<ICustomerUser[]>(PRIVATE_ENDPOINTS.adminUser.list, params),
  create: (data: object) => postData<ICustomerUser>(PRIVATE_ENDPOINTS.adminUser.list, data),
  update: (id: number, data: object) =>
    patchData<ICustomerUser>(PRIVATE_ENDPOINTS.adminUser.byId(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.adminUser.byId(id)),
}

/**
 * 👥 User API Service
 */
export const UserService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IUserInformation>>(PRIVATE_ENDPOINTS.user.all, params),
  getMe: () => getData<IUserInformation>(PRIVATE_ENDPOINTS.user.me),
  create: (data: object) => postData<IUserInformation>(PRIVATE_ENDPOINTS.user.create, data),
  sendVerificationEmail: () => getData(PRIVATE_ENDPOINTS.user.sendVerificationEmail),
  verifyEmail: () => getData(PRIVATE_ENDPOINTS.user.verifyEmail),
  update: (id: number, data: object) =>
    patchData<IUserInformation>(PRIVATE_ENDPOINTS.user.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.user.delete(id)),
}

/**
 * 📣 Advertising API Service
 */
export const AdvertisingService = {
  getAdvertisers: (params?: object) =>
    getData<IPaginatedResponse<IAdvertiser>>(PRIVATE_ENDPOINTS.advertiser.all, params),
  getSingleAdvertiser: (id: number) =>
    getData<IAdvertiser>(PRIVATE_ENDPOINTS.advertiser.single(id)),
  getAdvertiserCount: () => getData<number>(PRIVATE_ENDPOINTS.advertiser.count),
  createAdvertiser: (data: object) =>
    postData<IAdvertiser>(PRIVATE_ENDPOINTS.advertiser.create, data),
  updateAdvertiser: (id: number, data: object) =>
    patchData<IAdvertiser>(PRIVATE_ENDPOINTS.advertiser.update(id), data),
  deleteAdvertiser: (id: number) => deleteData(PRIVATE_ENDPOINTS.advertiser.delete(id)),
  getCampaigns: (params?: object) =>
    getData<IPaginatedResponse<ICampaign>>(PRIVATE_ENDPOINTS.campaign.all, params),
  getSingleCampaign: (id: number) => getData<ICampaign>(PRIVATE_ENDPOINTS.campaign.single(id)),
  getCampaignCount: () => getData<number>(PRIVATE_ENDPOINTS.campaign.count),
  createCampaign: (data: object) => postData<ICampaign>(PRIVATE_ENDPOINTS.campaign.create, data),
  updateCampaign: (id: number, data: object) =>
    patchData<ICampaign>(PRIVATE_ENDPOINTS.campaign.update(id), data),
  deleteCampaign: (id: number) => deleteData(PRIVATE_ENDPOINTS.campaign.delete(id)),
  getAdvs: (params?: object) =>
    getData<IPaginatedResponse<IAdv>>(PRIVATE_ENDPOINTS.adv.all, params),
  getSingleAdv: (id: number) => getData<IAdv>(PRIVATE_ENDPOINTS.adv.single(id)),
  getAdvCount: () => getData<number>(PRIVATE_ENDPOINTS.adv.count),
  createAdv: (data: object) => postData<IAdv>(PRIVATE_ENDPOINTS.adv.create, data),
  updateAdv: (id: number, data: object) => patchData<IAdv>(PRIVATE_ENDPOINTS.adv.update(id), data),
  deleteAdv: (id: number) => deleteData(PRIVATE_ENDPOINTS.adv.delete(id)),
}

/**
 * 🏌️‍♂️ Courses API Service
 */
export const CourseService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ICourse>>(PRIVATE_ENDPOINTS.course.all, params),
  getSingle: (id: number, params?: object) =>
    getData<ICourse>(PRIVATE_ENDPOINTS.course.single(id), params),
}

/**
 * 🏌️‍♂️ Holes API Service
 */
export const HoleService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IHole>>(PRIVATE_ENDPOINTS.hole.all, params),
  getSingle: (id: number) => getData<IHole>(PRIVATE_ENDPOINTS.hole.single(id)),
  getGeoJson: (id: number) => getData<IGeoJson>(PRIVATE_ENDPOINTS.hole.geojson(id)),
  update: (id: number, data: object) => patchData<IHole>(PRIVATE_ENDPOINTS.hole.update(id), data),
}

/**
 * 🛒 Customer API Service
 */
export const CustomerService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ICustomer>>(PRIVATE_ENDPOINTS.customer.all, params),
  getSingle: (id: number) => getData<ICustomer>(PRIVATE_ENDPOINTS.customer.single(id)),
  getStripeInfo: (id: number) => getData<IStripeInfo>(PRIVATE_ENDPOINTS.customer.stripeInfo(id)),
  create: (data: object) => postData<ICustomer>(PRIVATE_ENDPOINTS.customer.create, data),
  createStripeCustomer: (id: number, data: object) =>
    postData(PRIVATE_ENDPOINTS.customer.createStripeCustomer(id), data),
  createStripeSubscription: (id: number, data: object) =>
    postData(PRIVATE_ENDPOINTS.customer.createStripeSubscription(id), data),
  update: (id: number, data: object) =>
    patchData<ICustomer>(PRIVATE_ENDPOINTS.customer.update(id), data),
  addManager: (id: number, email: string) =>
    postData(PRIVATE_ENDPOINTS.customer.addManager(id, email), {}),
  deleteManager: (id: number, email: string) =>
    deleteData(PRIVATE_ENDPOINTS.customer.deleteManager(id, email)),
}

/**
 * 📍 Targets API Service
 */
export const TargetService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ITarget>>(PRIVATE_ENDPOINTS.target.all, params),
  getSingle: (id: number) => getData<ITarget>(PRIVATE_ENDPOINTS.target.single(id)),
  create: (data: object) => postData<ITarget>(PRIVATE_ENDPOINTS.target.create, data),
  getSchedules: (params?: object) =>
    getData<Pick<ITarget, 'valid_from'>[]>(PRIVATE_ENDPOINTS.target.schedules, params),
  applyLayoutTargetPreset: (data: object) =>
    postData(PRIVATE_ENDPOINTS.target.applyLayoutTargetPreset, data),
}

/**
 * 📱 Device API Service
 */
export const DeviceService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IDevice>>(PRIVATE_ENDPOINTS.device.all, params),
  getSingle: (id: number) => getData<IDevice>(PRIVATE_ENDPOINTS.device.single(id)),
  create: (data: object) => postData<IDevice>(PRIVATE_ENDPOINTS.device.create, data),
  update: (id: number, data: object) =>
    patchData<IDevice>(PRIVATE_ENDPOINTS.device.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.device.delete(id)),
}

/**
 * 📱 Tracker API Service
 */
export const TrackerService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ITrackerPosition>>(PRIVATE_ENDPOINTS.tracker.all, params),
  getActiveDays: (params: { month: string; id_golfclub: number }) =>
    getData<{ data: string[] }>(PRIVATE_ENDPOINTS.tracker.activeDays, params),
}

export const UserTrackerService = {
  getAll: (params?: object) => getData(PRIVATE_ENDPOINTS.user_tracker.all, params),
  readMessage: (id: number, data: object) =>
    patchData(PRIVATE_ENDPOINTS.user_tracker.readMessage(id), data),
  sendNotification: (data: object) =>
    postData(PRIVATE_ENDPOINTS.user_tracker.sendNotification, data),
}

export const DangerService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IDanger>>(PRIVATE_ENDPOINTS.danger.all, params),
  create: (data: object) => postData<IDanger>(PRIVATE_ENDPOINTS.danger.create, data),
  update: (id: number, data: object) =>
    patchData<IDanger>(PRIVATE_ENDPOINTS.danger.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.danger.delete(id)),
}

/**
 * Layout API Service
 */
export const LayoutService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ILayout>>(PRIVATE_ENDPOINTS.layout.all, params),
  getSingle: (id: number) => getData<ILayout>(PRIVATE_ENDPOINTS.layout.single(id)),
  create: (data: object) => postData<ILayout>(PRIVATE_ENDPOINTS.layout.create, data),
  update: (id: number, data: object) =>
    patchData<ILayout>(PRIVATE_ENDPOINTS.layout.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.layout.delete(id)),
}

/**
 * Layout print API Service
 */
export const LayoutPrintService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ILayoutPrint>>(PRIVATE_ENDPOINTS.layout_print.all, params),
  getSingle: (id: number) => getData<ILayoutPrint>(PRIVATE_ENDPOINTS.layout_print.single(id)),
  create: (data: object) => postData(PRIVATE_ENDPOINTS.layout_print.create, data),
  update: (data: object) => patchData(PRIVATE_ENDPOINTS.layout_print.update, data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.layout_print.delete(id)),
}

/**
 * Layout target preset API Service
 */
export const LayoutTargetPresetService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ILayoutTargetPreset>>(
      PRIVATE_ENDPOINTS.layout_target_preset.all,
      params,
    ),
  getSingle: (id: number) =>
    getData<ILayoutTargetPreset>(PRIVATE_ENDPOINTS.layout_target_preset.single(id)),
  create: (data: object) => postData(PRIVATE_ENDPOINTS.layout_target_preset.create, data),
  update: (data: object) => patchData(PRIVATE_ENDPOINTS.layout_target_preset.update, data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.layout_target_preset.delete(id)),
}

/**
 * 🏌️‍♂️ Golf Partner API Service
 */
export const PartnerService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IPartner>>(PRIVATE_ENDPOINTS.partner.all, params),
  create: (data: object) => postData<IPartner>(PRIVATE_ENDPOINTS.partner.create, data),
  update: (id: number, data: object) =>
    patchData<IPartner>(PRIVATE_ENDPOINTS.partner.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.partner.delete(id)),
  getClubs: (params?: object) => getData(PRIVATE_ENDPOINTS.partner.clubs, params),
  getSingleClub: (id: number) => getData(PRIVATE_ENDPOINTS.partner.singleClub(id)),
  getClubGeoJson: (id: number) => getData(PRIVATE_ENDPOINTS.partner.clubGeoJson(id)),
}

export const LeadingCourseService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ILcClub>>(PRIVATE_ENDPOINTS.leadingCourse.all, params),
  getSingle: (id: number) => getData<ILcClub>(PRIVATE_ENDPOINTS.leadingCourse.single(id)),
  create: (data: object) => postData<ILcClub>(PRIVATE_ENDPOINTS.leadingCourse.create, data),
  update: (id: number, data: object) =>
    patchData<ILcClub>(PRIVATE_ENDPOINTS.leadingCourse.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.leadingCourse.delete(id)),
}

export const CourseRevService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ICourseRevGolfclub>>(PRIVATE_ENDPOINTS.courseRev.all, params),
  getSingle: (id: number) => getData<ICourseRevGolfclub>(PRIVATE_ENDPOINTS.courseRev.single(id)),
}

export const NotificationService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<INotification>>(PRIVATE_ENDPOINTS.notification.all, params),
  create: (data: object) => postForm(PRIVATE_ENDPOINTS.notification.create, data),
  update: (id: number, data: object) =>
    patchData<INotification>(PRIVATE_ENDPOINTS.notification.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.notification.delete(id)),
}

export const EventService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IUserEvent>>(PRIVATE_ENDPOINTS.event.all, params),
  getSingle: (id: number) => getData<IUserEvent>(PRIVATE_ENDPOINTS.event.single(id)),
  create: (data: object) => postData<IUserEvent>(PRIVATE_ENDPOINTS.event.create, data),
  update: (id: number, data: object) =>
    patchData<IUserEvent>(PRIVATE_ENDPOINTS.event.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.event.delete(id)),
  getStatsByGolfclub: (params?: { days?: number }) =>
    getData<
      {
        id_golfclub: number
        golfclub_name: string | null
        title: string
        count: number
      }[]
    >(PRIVATE_ENDPOINTS.event.statsByGolfclub, params),
}

export const TopicService = {
  getAll: (params?: object) =>
    getData<IPaginatedResponse<IUserTopic>>(PRIVATE_ENDPOINTS.topic.all, params),
  getSingle: (id: number) => getData<IUserTopic>(PRIVATE_ENDPOINTS.topic.single(id)),
  create: (data: object) => postData<IUserTopic>(PRIVATE_ENDPOINTS.topic.create, data),
  update: (id: number, data: object) =>
    patchData<IUserTopic>(PRIVATE_ENDPOINTS.topic.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.topic.delete(id)),
}

export const MaintenanceService = {
  getActive: (params?: object) =>
    getData<ISystemMaintenance[]>(PRIVATE_ENDPOINTS.maintenance.active, params),
  getAll: (params?: object) =>
    getData<IPaginatedResponse<ISystemMaintenance>>(PRIVATE_ENDPOINTS.maintenance.all, params),
  getSingle: (id: number) => getData<ISystemMaintenance>(PRIVATE_ENDPOINTS.maintenance.single(id)),
  create: (data: object) =>
    postData<ISystemMaintenance>(PRIVATE_ENDPOINTS.maintenance.create, data),
  update: (id: number, data: object) =>
    patchData<ISystemMaintenance>(PRIVATE_ENDPOINTS.maintenance.update(id), data),
  delete: (id: number) => deleteData(PRIVATE_ENDPOINTS.maintenance.delete(id)),
}

/**
 * 📡 IOTee API Service
 */
export const IoteeService = {
  getSites: (params?: object) =>
    getData<IPaginatedResponse<IIoteeSite>>(PRIVATE_ENDPOINTS.iotee.sites, params),
  getDevices: (params?: object) =>
    getData<IPaginatedResponse<IIoteeDevice>>(PRIVATE_ENDPOINTS.iotee.devices, params),
  updateSite: (id: number, data: { id_golfclub: number | null }) =>
    patchData<IIoteeSite>(PRIVATE_ENDPOINTS.iotee.updateSite(id), data),
  syncSites: () => getData<IIoteeSyncSitesResult>(PRIVATE_ENDPOINTS.iotee.syncSites),
  syncDevices: (siteId: string) =>
    getData<IIoteeSyncDevicesResult>(PRIVATE_ENDPOINTS.iotee.syncDevices(siteId)),
  loadDeviceHistory: (
    siteId: string,
    deviceId: string,
    params?: { start?: string; end?: string },
  ) =>
    getData<IIoteeLoadHistoryResult>(
      PRIVATE_ENDPOINTS.iotee.loadDeviceHistory(siteId, deviceId),
      params,
    ),
}
